import React, { useCallback, useState, useEffect } from "react";

import {
  ReactFlow,
  addEdge,
  Background,
  MiniMap,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  getBezierPath,
  useReactFlow,
  EdgeLabelRenderer,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  FormControl,
  StyledSelect,
  StyledInput,
  Label,
} from "../../Form/FormControls";
import { MdRemoveCircle, MdFeed } from "react-icons/md";
import { v4 as uuid } from "uuid";
import { format } from "date-fns";
import SelectTags from "../../Tags/SelectTags";
import UpdateTags from "../../Tags/UpdateTags";
import { getAllDataSources } from "../../../api/dataSourceQueries";
import { getAllServicerTransferFeeds } from "../../../api/serviceTransferQueries";
import { useApi } from "../../../api/useApi";
import { sortByAlpha } from "../../../common/helpers/util";
import EtlPipelineSelector from "./CreateForm/Actions/EtlPipelineActionFlow";
import { Button, IconButton } from "@mui/material";
import dagre from "dagre";
import { FaDatabase } from "react-icons/fa";
import { AiOutlineCluster } from "react-icons/ai";
import { createWorkflow, updateWorkflow } from "../../../api/workflowMutations";
import ShowSuccessModal from "./CreateForm/ShowSuccessModal";
import ErrorMessages from "../../Notifications/ErrorMessages";

// ADD SEARCH FIELD TO HIGHLIGHT ETL OR DATASOURCE PIPELINE
const minuteOptions = [
  { value: "0", label: ":00" },
  { value: "1", label: ":15" },
  { value: "2", label: ":30" },
  { value: "3", label: ":45" },
];

const ampmOptions = [
  { value: true, label: "AM" },
  { value: false, label: "PM" },
];

const frequencyOptions = [
  { value: "HOURLY", label: "Hourly" },
  { value: "QUARTER_DAILY", label: "Every 6 Hours" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

const weekdayOptions = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
];

export const dayOptions = [
  { value: 32, label: "EOM" },
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
  { value: 13, label: "13th" },
  { value: 14, label: "14th" },
  { value: 15, label: "15th" },
  { value: 16, label: "16th" },
  { value: 17, label: "17th" },
  { value: 18, label: "18th" },
  { value: 19, label: "19th" },
  { value: 20, label: "20th" },
  { value: 21, label: "21st" },
  { value: 22, label: "22nd" },
  { value: 23, label: "23rd" },
  { value: 24, label: "24th" },
  { value: 25, label: "25th" },
  { value: 26, label: "26th" },
  { value: 27, label: "27th" },
  { value: 28, label: "28th" },
  { value: 29, label: "29th" },
  { value: 30, label: "30th" },
  { value: 31, label: "31st" },
];

const hourOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6" },
  { value: "7", label: "7" },
  { value: "8", label: "8" },
  { value: "9", label: "9" },
  { value: "10", label: "10" },
  { value: "11", label: "11" },
  { value: "12", label: "12" },
];

const getZone = format(new Date(), "XXX", {
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
}) => {
  // Calculate the edge path and label position
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Access React Flow's setEdges function
  const { setEdges } = useReactFlow();

  // Handler to remove the edge
  const onRemove = () => setEdges((eds) => eds.filter((e) => e.id !== id));

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
          className="nodrag nopan"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              borderRadius: "4px",
              padding: "2px 4px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          >
            <span style={{ marginRight: "4px" }}>{data.label}</span>
            <button
              title="Remove Edge"
              onClick={onRemove}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdRemoveCircle color="red" size={16} />
            </button>
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes = {
  custom: CustomEdge,
};

const IconSet = (entityType) => {
  switch (entityType) {
    case "DATA_SOURCE":
      return <FaDatabase />;
    case "ETL_PIPELINE":
      return <AiOutlineCluster />;
    case "TRANSFORMATION":
      return <MdFeed />;
    default:
      return null;
  }
};

// Define CustomNode first
const CustomNode = ({ id, data }) => {
  const { label, type, dataQualityThreshold, entityName, onRemove, setNodes } =
    data;

  const handleThresholdChange = (event) => {
    const newValue = event.target.value;

    // Update the node's data using setNodes
    setNodes((prevNodes) =>
      prevNodes.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                dataQualityThreshold: newValue, // Update the specific field
              },
            }
          : node
      )
    );
  };
  return (
    <div
      style={{
        padding: 10,
        border: "1px solid #ddd",
        borderRadius: 5,
        backgroundColor: "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ marginTop: "0.36rem", marginRight: "1rem" }}>
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: ".2rem" }}>{IconSet(label)}</div>
            {label}
          </div>
          <div
            style={{ maxWidth: "200px", fontSize: ".8rem", marginTop: ".2rem" }}
          >
            {entityName}
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            flexDirection: "column",
            alignContent: "right",
          }}
        >
          <div style={{ textAlign: "right" }}>
            <IconButton
              variant="contained"
              type="button"
              onClick={() => onRemove(id)}
              size={"small"}
              sx={{
                fontSize: 12,
                background: "white",
                color: "red",
              }}
              title={"Remove Node"}
            >
              <MdRemoveCircle />
            </IconButton>
          </div>
          {type === "DATA_SOURCE" ? (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <strong> Min. DQ Score:</strong>
              </div>
              <input
                type="number"
                value={dataQualityThreshold}
                max={100}
                min={0}
                onChange={handleThresholdChange}
                style={{
                  width: "80px",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  marginTop: "4px",
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <Handle
        type="source" // Outgoing connections
        position="bottom"
        style={{
          background: "#555",
          width: 10,
          height: 10,
          borderRadius: "50%",
        }}
      />
      <Handle
        type="target" // Incoming connections
        position="top"
        style={{
          background: "#555",
          width: 10,
          height: 10,
          borderRadius: "50%",
        }}
      />
    </div>
  );
};

// Now define nodeTypes after CustomNode
const nodeTypes = { customNode: CustomNode };

const dependencyTypes = ["DATA_SOURCE", "ETL_PIPELINE", "TRANSFORMATION"];

const statusOptions = ["Pending", "Running", "Completed", "Failed"];

const WorkflowBuilder = ({ workflowId, workflowData: initialWorkflow }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  let triggerTimestamp = initialWorkflow?.alertTriggerTimestamp
    ? new Date(initialWorkflow?.alertTriggerTimestamp)
    : new Date();

  const [workflowName, setWorkflowName] = useState(initialWorkflow?.name || "");
  const [frequency, setFrequency] = useState(
    frequencyOptions.find(
      (m) => m.value === initialWorkflow?.alertTriggerTime.frequency
    ) || frequencyOptions[2]
  );
  const [weekday, setWeekday] = useState(
    weekdayOptions.find(
      (m) => m.value === initialWorkflow?.alertTriggerTime.weekday
    ) || weekdayOptions[0]
  );
  const [day, setDay] = useState(
    dayOptions.find(
      (m) => Number(m.value) === initialWorkflow?.alertTriggerTime.day
    ) || dayOptions[0]
  );
  const [zone, setZone] = useState(getZone);
  const [hour, setHour] = useState(
    hourOptions.find((m) => m.value === format(triggerTimestamp, "h")) ||
      hourOptions[0]
  );
  const [min, setMin] = useState(
    minuteOptions.find(
      (m) => m.value === String(Number(format(triggerTimestamp, "m")) / 15)
    ) || minuteOptions[0]
  );
  const [isAm, setIsAm] = useState(
    ampmOptions.find(
      (m) => m.value === (format(triggerTimestamp, "a") === "AM")
    ) || ampmOptions[0]
  );
  const [success, setShowSuccess] = useState(false);
  const [tagInstances, setTagInstances] = useState([]);

  const [selectedDependencyType, setSelectedDependencyType] = useState(null);

  const [nodeData, setNodeData] = useState({
    entityId: "",
  });

  const [{ errors: createErrors, data: createData }, create] = useApi();

  // start listening to process for create if success
  useEffect(() => {
    if (createData && !createErrors?.length) {
      setShowSuccess(true);
    } else if (createErrors?.length) {
      setShowSuccess(false);
    }
  }, [createData, createErrors]);

  useEffect(() => {
    if (initialWorkflow) loadWorkflow(initialWorkflow);
  }, [initialWorkflow]);

  const applyTreeLayout = (nodes, edges) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: "TB", align: "UL" }); // Top-to-Bottom layout

    // Add nodes to the Dagre graph
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 250, height: 150 }); // Adjust dimensions
    });

    // Add edges to the Dagre graph
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Perform Dagre layout
    dagre.layout(dagreGraph);

    // Update node positions based on Dagre's layout
    const updatedNodes = nodes.map((node) => {
      const dagreNode = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: dagreNode.x - 100, // Center adjustment
          y: dagreNode.y,
        },
        draggable: true, // Lock position for clean layout
      };
    });

    return updatedNodes;
  };

  // Apply this layout after loading workflow
  const loadWorkflow = (workflow) => {
    if (!workflow.dependencies) return;

    const dependencies = workflow.dependencies;

    // Step 1: Create nodes
    const nodes = dependencies.map((dependency) => ({
      id: `${dependency.type}-${dependency.id}`, // Combine type and id
      data: {
        label: dependency.type,
        type: dependency.type,
        entityName: dependency.friendlyName || dependency.name,
        entityId: dependency.id,
        dataQualityThreshold: dependency.dataSourceDataQualityThreshold,
        etlProviderInstanceId: dependency.etlProviderInstanceId,
      },
      position: { x: 0, y: 0 }, // Placeholder
      type: "customNode",
    }));

    const createEdges = (dependencyArray, targetId, type) =>
      dependencyArray.map((dep) => ({
        id: `e${type}-${dep.id}-${targetId}`, // Unique edge ID
        source: `${type}-${dep.id}`, // Match node IDs with type-prefix
        target: targetId, // Target node ID (already includes the type prefix)
        type: "custom", // Custom edge type
        animated: true,
        data: { label: "" },
        markerEnd: { type: "arrowclosed", color: "black" }, // Arrow points to the target
      }));

    // Step 2: Create edges
    const edges = dependencies.flatMap((dependency) => {
      const targetId = `${dependency.type}-${dependency.id}`; // Match the node ID format
      return [
        ...createEdges(
          dependency.dataSourceDependencies,
          targetId,
          "DATA_SOURCE"
        ),
        ...createEdges(
          dependency.pipelineDependencies,
          targetId,
          "ETL_PIPELINE"
        ),
        ...createEdges(
          dependency.transformationDependencies,
          targetId,
          "TRANSFORMATION"
        ),
      ];
    });

    // Step 3: Apply layout
    const layoutNodes = applyTreeLayout(nodes, edges);

    // Update state
    setNodes(layoutNodes);
    setEdges(edges);
  };

  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: uuid(),
        type: "custom", // Use custom edge type
        animated: true,
        label: "",
        data: {
          label: "",
          onRemove: handleRemoveEdge,
        },
        markerEnd: { type: "arrowclosed", color: "black" },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const addDependencyNode = () => {
    if (!selectedDependencyType)
      return alert("Select a dependency type first!");

    if (selectedDependencyType !== "ETL_PIPELINE" && !nodeData.entityId)
      return alert("Select an entity first!");

    if (
      selectedDependencyType === "ETL_PIPELINE" &&
      !nodeData.etlProviderInstanceId
    )
      return alert("Select a provider first!");

    if (selectedDependencyType === "ETL_PIPELINE" && !nodeData.entityName)
      return alert("Select an instance first!");

    if (
      selectedDependencyType === "DATA_SOURCE" &&
      (!nodeData.dataQualityThreshold ||
        nodeData.dataQualityThreshold < 0 ||
        nodeData.dataQualityThreshold > 100)
    )
      return alert("Enter a data quality threshold between 0 and 100!");

    const newNode = {
      id: `${selectedDependencyType}-${uuid()}`, // Combine type and a unique identifier
      type: "customNode",
      data: {
        label: selectedDependencyType,
        type: selectedDependencyType,
        entityId: nodeData.entityId,
        ...nodeData, // Include all nodeData fields
      },
      position: { x: Math.random() * 400, y: Math.random() * 400 },
    };
    setNodes((nds) => nds.concat(newNode));
    resetForm();
  };

  const resetForm = () => {
    setSelectedDependencyType(null);
    setNodeData({
      entityId: "",
    });
  };

  const removeNode = (nodeId) => {
    const updatedNodes = nodes.filter((node) => node.id !== nodeId);
    const updatedEdges = edges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  };

  const updateEdgeStatus = (edgeId, newStatus) => {
    setEdges((eds) =>
      eds.map((edge) =>
        edge.id === edgeId
          ? { ...edge, data: { ...edge.data, label: newStatus } }
          : edge
      )
    );
  };

  const handleRemoveEdge = (id) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
  };

  const handleTagChange = React.useCallback(
    (tags) => {
      setTagInstances(tags);
    },
    [setTagInstances]
  );

  function flattenDepObjects(dependencies) {
    const cleanDeps = dependencies.map((dep, i) => {
      return {
        name: dep?.name,
        type: dep?.type,
        ...(dep?.type !== "ETL_PIPELINE" && {
          id: dep?.id,
        }),
        ...(dep?.type === "ETL_PIPELINE" && {
          etlProviderInstanceId: dep?.etlProviderInstanceId,
          id: dep?.etlProviderInstanceId,
        }),
        ...(dep?.type === "DATA_SOURCE" && {
          dataSourceDataQualityThreshold: Number(dep?.dataQualityThreshold),
        }),
        dataSourceDependencies: dep.dataSourceDependencies.map((dsDep) => {
          return {
            id: dsDep?.id,
            name: dsDep?.name,
          };
        }),
        pipelineDependencies: dep.pipelineDependencies.map((dsDep) => {
          return {
            name: dsDep?.name,
            etlProviderInstanceId: dsDep?.etlProviderInstanceId,
          };
        }),
        transformationDependencies: dep.transformationDependencies.map(
          (dsDep) => {
            return {
              id: dsDep?.id,
            };
          }
        ),
      };
    });

    return cleanDeps;
  }

  const generateSubmissionObject = () => {
    const nodeMap = {};
    nodes.forEach((node) => {
      nodeMap[node.id] = node;
    });

    const dependencyMap = {};
    edges.forEach((edge) => {
      const { source, target } = edge;
      if (!dependencyMap[target]) {
        dependencyMap[target] = [];
      }
      dependencyMap[target].push(source);
    });

    const dependencies = nodes.map((node) => {
      const dependency = {
        ...node.data,
        name: node.data.entityName,
        id: node.data.entityId,
        type: node.data.type,
        dataSourceDependencies: [],
        pipelineDependencies: [],
        transformationDependencies: [],
      };

      // Add parent nodes as dependencies based on their type
      if (dependencyMap[node.id]) {
        dependencyMap[node.id].forEach((parentId) => {
          const parentNode = nodeMap[parentId];
          const parentDependency = {
            id: parentNode.data.entityId,
            name: parentNode.data.entityName,
            type: parentNode.data.type,
          };

          // Add parent to the appropriate type-specific dependency field
          if (parentNode.data.type === "DATA_SOURCE") {
            dependency.dataSourceDependencies.push(parentDependency);
          } else if (parentNode.data.type === "ETL_PIPELINE") {
            dependency.pipelineDependencies.push(parentDependency);
          } else if (parentNode.data.type === "TRANSFORMATION") {
            dependency.transformationDependencies.push(parentDependency);
          }
        });
      }

      return dependency;
    });

    //update workflow vs save new
    if (initialWorkflow) {
      const submitObject = {
        name: workflowName,
        dependencies: flattenDepObjects(dependencies),
        alertTriggerTime: {
          frequency: frequency?.value,
          weekday: weekday?.value,
          day: Number(day?.value),
          hour: Number(hour?.value),
          minutes: Number(min?.value),
          isAM: isAm?.value,
          timeZone: zone,
        },
        ...(initialWorkflow && {
          tagInstanceInputs: tagInstances,
        }),
      };

      create({ query: updateWorkflow, variables: { workflow: submitObject } });
    } else {
      const submitObject = {
        name: workflowName,
        dependencies: flattenDepObjects(dependencies),
        alertTriggerTime: {
          frequency: frequency?.value,
          weekday: weekday?.value,
          day: Number(day?.value),
          hour: Number(hour?.value),
          minutes: Number(min?.value),
          isAM: isAm?.value,
          timeZone: zone,
        },
        ...(initialWorkflow && {
          tagInstanceInputs: tagInstances,
        }),
      };

      create({ query: createWorkflow, variables: { workflow: submitObject } });
    }
  };

  const [{ errors: allSourceErrors, loading: isLoading, data: sources }] =
    useApi(getAllDataSources);
  //for some reason tagFilter is required on this query... why.
  const [
    {
      errors: allTransformationErrors,
      loading: isLoadingTransformations,
      data: transformations,
    },
  ] = useApi(getAllServicerTransferFeeds, {
    tagFilter: {
      tagIds: [],
      exactMatch: false,
    },
    where: {
      enabled: { eq: true },
    },
  });

  const sortedDataSources = sources?.availableWorkflowDataSources?.length
    ? sortByAlpha(sources?.availableWorkflowDataSources, "name")
    : [];
  const sortedDataSourcesOptions = sortedDataSources.map((sds) => {
    return {
      label: sds?.friendlyName ?? sds?.name,
      value: sds?.id,
    };
  });
  const sortedTransformations = transformations?.allServicerTransferFeeds?.edges
    ?.length
    ? sortByAlpha(
        transformations?.allServicerTransferFeeds?.edges.map((sf) => sf.node),
        "name"
      )
    : [];

  const sortedTransformationsOptions = sortedTransformations.map((st) => {
    return {
      label: st?.name,
      value: st?.id,
    };
  });

  // Define form fields for each dependency type
  const renderFormFields = () => {
    switch (selectedDependencyType) {
      case "DATA_SOURCE":
        return (
          <>
            <FormControl>
              <Label>Select DataSource</Label>
              <StyledSelect
                className={`react-select-container`}
                classNamePrefix={`react-select`}
                name={`source`}
                id={`min`}
                inputId={`minSelect-input`}
                instanceId={`minSelect-instance`}
                label="Minutes"
                options={sortedDataSourcesOptions}
                placeholder={`Select DataSource`}
                menuPortalTarget={document.body}
                value={
                  sortedDataSourcesOptions?.find(
                    (ds) => ds.value === nodeData.entityId
                  ) ?? null
                }
                menuPlacement="auto"
                onChange={(e) =>
                  setNodeData({
                    ...nodeData,
                    entityId: e?.value,
                    entityName: e?.label,
                  })
                }
              />
            </FormControl>

            <FormControl>
              <Label>Data Quality Threshold</Label>
              <StyledInput
                type="number"
                value={nodeData.dataQualityThreshold || ""}
                onChange={(e) =>
                  setNodeData({
                    ...nodeData,
                    dataQualityThreshold: e.target.value,
                  })
                }
                placeholder="Data Quality Threshold"
                style={{ marginTop: ".5rem" }}
              />
            </FormControl>
          </>
        );
      case "ETL_PIPELINE":
        const usedEtlPipelines = nodes
          .filter((node) => node.data.type === "ETL_PIPELINE" && node.data.name)
          .map((node) => ({ name: node.data.name }));

        return (
          <>
            <EtlPipelineSelector
              nodeData={nodeData}
              setNodeData={setNodeData}
              usedEtlPipelines={usedEtlPipelines}
            />
          </>
        );
      case "TRANSFORMATION":
        return (
          <>
            <StyledSelect
              className={`react-select-container`}
              classNamePrefix={`react-select`}
              name={`transformation`}
              id={`transformation`}
              inputId={`transformation-input`}
              instanceId={`transformation-instance`}
              label="Transformations"
              options={sortedTransformationsOptions}
              placeholder={`Select Transformation`}
              menuPortalTarget={document.body}
              value={
                sortedTransformationsOptions?.find(
                  (ds) => ds.value === nodeData.entityId
                ) ?? null
              }
              menuPlacement="auto"
              onChange={(e) =>
                setNodeData({
                  ...nodeData,
                  entityId: e?.value,
                  entityName: e?.label,
                })
              }
            />
          </>
        );
      default:
        return null;
    }
  };

  const proOptions = { hideAttribution: true };
  return (
    <>
      {success ? (
        <ShowSuccessModal
          workflowData={initialWorkflow}
          setShowSuccess={setShowSuccess}
        />
      ) : null}

      <div
        style={{
          height: "calc(100vh - 150px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="control-panel">
          <FormControl>
            {initialWorkflow ? (
              <>
                <h3>{workflowName}</h3>
              </>
            ) : (
              <StyledInput
                type="text"
                value={workflowName}
                label="Workflow Name"
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="Workflow Name"
                style={{ marginRight: "10px" }}
              />
            )}
          </FormControl>
          <div
            style={{ flex: 1, flexDirection: "column", marginRight: "1rem" }}
          >
            <>
              {initialWorkflow?.alertTriggerTimestamp ? (
                <div style={{ marginBottom: ".5rem" }}>
                  <b>Current Alert Time: </b>
                </div>
              ) : null}

              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "170px",
                    display: "inline-block",
                  }}
                >
                  <StyledSelect
                    className={`react-select-container`}
                    classNamePrefix={`react-select`}
                    name={`frequency`}
                    id={`frequency`}
                    inputId={`frequencySelect-input`}
                    instanceId={`frequencySelect-instance`}
                    label="Frequency"
                    options={frequencyOptions}
                    menuPortalTarget={document.body}
                    placeholder={`Select Frequency`}
                    value={frequency}
                    menuPlacement="auto"
                    onChange={(e) =>
                      setFrequency(
                        frequencyOptions.find((m) => m.value === e.value)
                      )
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        width: "160px",
                        marginRight: "1rem",
                      }),
                    }}
                  />
                </div>
                {frequency.value === "WEEKLY" ? (
                  <div
                    style={{
                      width: "170px",
                      display: "inline-block",
                    }}
                  >
                    <StyledSelect
                      className={`react-select-container`}
                      classNamePrefix={`react-select`}
                      name={`weekday`}
                      id={`weekday`}
                      inputId={`weekdaySelect-input`}
                      instanceId={`weekdaySelect-instance`}
                      label="weekday"
                      options={weekdayOptions}
                      menuPortalTarget={document.body}
                      placeholder={`Select Weekday`}
                      value={weekday}
                      menuPlacement="auto"
                      onChange={(e) =>
                        setWeekday(
                          weekdayOptions.find((m) => m.value === e.value)
                        )
                      }
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          width: "140px",
                          marginRight: "1rem",
                        }),
                      }}
                    />
                  </div>
                ) : null}
                {frequency.value === "MONTHLY" ? (
                  <div
                    style={{
                      width: "170px",
                      display: "inline-block",
                    }}
                  >
                    <StyledSelect
                      className={`react-select-container`}
                      classNamePrefix={`react-select`}
                      name={`day`}
                      id={`day`}
                      inputId={`daySelect-input`}
                      instanceId={`daySelect-instance`}
                      label="day"
                      options={dayOptions}
                      menuPortalTarget={document.body}
                      placeholder={`Select day`}
                      value={day}
                      menuPlacement="auto"
                      onChange={(e) =>
                        setDay(dayOptions.find((m) => m.value === e.value))
                      }
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          width: "100px",
                          marginRight: "1rem",
                        }),
                      }}
                    />
                  </div>
                ) : null}
                <div
                  style={{
                    width: "100px",
                    marginRight: "1rem",
                    display: "inline-block",
                  }}
                >
                  <StyledSelect
                    className={`react-select-container`}
                    classNamePrefix={`react-select`}
                    name={`hour`}
                    id={`hour`}
                    inputId={`hourSelect-input`}
                    instanceId={`hourSelect-instance`}
                    label="Hours"
                    options={hourOptions}
                    placeholder={`Select Hours`}
                    menuPortalTarget={document.body}
                    value={
                      frequency.value === "HOURLY"
                        ? { value: hour.value, label: "N/A" }
                        : hour
                    }
                    menuPlacement="auto"
                    isDisabled={frequency.value === "HOURLY"}
                    onChange={(e) =>
                      setHour(hourOptions.find((h) => h.value === e.value))
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        width: "100px",
                      }),
                    }}
                  />
                </div>
                <div
                  style={{
                    width: "110px",
                    marginRight: "1rem",
                    display: "inline-block",
                  }}
                >
                  <StyledSelect
                    className={`react-select-container`}
                    classNamePrefix={`react-select`}
                    name={`min`}
                    id={`min`}
                    inputId={`minSelect-input`}
                    instanceId={`minSelect-instance`}
                    label="Minutes"
                    options={minuteOptions}
                    placeholder={`Select Minutes`}
                    menuPortalTarget={document.body}
                    value={min}
                    menuPlacement="auto"
                    onChange={(e) =>
                      setMin(minuteOptions.find((m) => m.value === e.value))
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        width: "110px",
                      }),
                    }}
                  />
                </div>
                <div
                  style={{
                    width: "110px",
                    marginRight: "1rem",
                    display: "inline-block",
                  }}
                >
                  <StyledSelect
                    className={`react-select-container`}
                    classNamePrefix={`react-select`}
                    name={`isAm`}
                    id={`isAm`}
                    inputId={`isAmSelect-input`}
                    instanceId={`isAmSelect-instance`}
                    label="AM/PM"
                    options={ampmOptions}
                    menuPortalTarget={document.body}
                    placeholder={`Select AM/PM`}
                    value={isAm}
                    menuPlacement="auto"
                    onChange={(e) =>
                      setIsAm(ampmOptions.find((m) => m.value === e.value))
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        width: "110px",
                      }),
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "inline-block",
                  }}
                >
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </div>
              </div>
            </>
          </div>

          {initialWorkflow ? (
            <div style={{ marginBottom: "1rem" }}>
              <UpdateTags remoteObjectId={workflowId} tagType={"WORKFLOW"} />
            </div>
          ) : (
            <div style={{ marginBottom: "1rem" }}>
              <h3>Tags</h3>
              <p>Select the appropriate tags below.</p>
              <SelectTags updateTags={handleTagChange} tagType={"WORKFLOW"} />
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              padding: "10px",
              border: "4px solid rgb(0, 159, 212)",
              borderRadius: "5px",
              backgroundColor: "rgb(255, 255, 255)",
              zIndex: 5,
              position: "absolute",
              margin: "1rem",
              maxWidth: "450px",
            }}
          >
            <FormControl>
              <h3>Workflow Creator</h3>
              <p>
                Select the type of node you want to add into your workflow. Then
                connect the edge by clicking on the dot and connecting it to
                another node.
              </p>
              <Label>Select Node Type</Label>
              <StyledSelect
                className={`react-select-container`}
                classNamePrefix={`react-select`}
                name={`nodeType`}
                id={`nodeType`}
                inputId={`nodeType-input`}
                instanceId={`nodeType-instance`}
                label="nodeType"
                options={dependencyTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                placeholder={`Select Node Type`}
                onChange={(e) => setSelectedDependencyType(e.value)}
                value={
                  dependencyTypes
                    .map((type) => ({
                      label: type,
                      value: type,
                    }))
                    .find((dt) => dt.value === selectedDependencyType) ?? null
                }
                menuPortalTarget={document.body}
                menuPlacement="auto"
              />
            </FormControl>

            {/* Render form fields based on selectedDependencyType */}
            {selectedDependencyType && renderFormFields()}

            <div
              style={{
                display: "flex",
                margin: "1rem",
                marginBottom: ".5rem",
              }}
            >
              <div style={{ marginLeft: "auto" }}>
                <Button
                  variant="contained"
                  type="button"
                  onClick={addDependencyNode}
                >
                  Add Node
                </Button>
              </div>
            </div>

            <h3>Save Workflow</h3>
            <p>
              Once you've added an connected your nodes, save your workflow by
              clicking the button below.
            </p>
            <div style={{ textAlign: "center" }}>
              <Button
                variant="contained"
                type="button"
                onClick={() => generateSubmissionObject()}
                sx={{
                  backgroundColor: "rgb(18, 17, 53)",
                  "&:hover": {
                    backgroundColor: "rgba(18, 17, 53, 0.8)",
                  },
                }}
              >
                Save Workflow
              </Button>
            </div>

            {createErrors ? <ErrorMessages errors={createErrors} /> : null}
          </div>

          <div
            style={{
              position: "absolute",
              right: 0,
              margin: "1rem",
              zIndex: 5,
            }}
          ></div>

          <ReactFlow
            nodes={nodes.map((node) => ({
              ...node,
              data: {
                ...node.data,
                onRemove: removeNode,
                setNodes, // Pass setNodes to the node's data
              },
            }))}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            edgeTypes={edgeTypes} // Register custom edge type
            nodeTypes={nodeTypes}
            proOptions={proOptions}
            fitView
          >
            <MiniMap />
            <Controls />
            <Background />
          </ReactFlow>
        </div>
      </div>
    </>
  );
};

export default WorkflowBuilder;
